import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { getPlatform } from '../utils/platform';

type Severity = 'critical' | 'error' | 'warning';

interface Breadcrumb {
  ts: number;
  type: string;
  data?: Record<string, string>;
}

interface ErrorReport {
  appVersion: string;
  buildNumber: string;
  bundleId: string;
  platform: 'ios' | 'android' | 'web';
  deviceModel?: string;
  osVersion?: string;
  userId?: string;
  sessionId: string;
  timestamp: number;
  severity: Severity;
  error: { name: string; message: string; stack?: string };
  breadcrumbs?: Breadcrumb[];
  context?: { route?: string; userAgent?: string; networkType?: string };
}

interface QueueItem {
  payload: ErrorReport;
  enqueuedAt: number;
}

// Errors API lives on the same host as every other dp-client API call —
// CloudFront fronts the host and path-routes /api9/errors/* to the mobile-errors
// Lambda. We derive the URL from GATSBY_API_URL (set per-environment in the
// build-cap-* scripts in package.json), so dev/staging/prod targeting flows
// automatically from the existing convention. No separate env var needed.
const ERRORS_PATH = '/api9/errors';
const AUTH_TOKEN_KEY = 'dentalpost_auth_token';
const USER_DATA_KEY = 'dentalpost_token_user_data';
const QUEUE_KEY = 'dp.errorQueue.v2';

const errorsBaseUrl = (): string | null => {
  const base = process.env.GATSBY_API_URL;
  if (!base) return null;
  return base.endsWith('/') ? base.slice(0, -1) + ERRORS_PATH : base + ERRORS_PATH;
};
const MAX_QUEUE = 50;
const MAX_BREADCRUMBS = 30;
const QUEUE_TTL_MS = 24 * 60 * 60 * 1000;
const BEACON_BUDGET_BYTES = 60 * 1024;
const FLUSH_CONCURRENCY = 3;

const PII_PATTERNS: Array<[RegExp, string]> = [
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, '<email>'],
  [/\+\d[\d\s().-]{7,}\d/g, '<phone-intl>'],
  [/\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/g, '<phone>'],
  [/\b(?:\d[ -]*?){13,19}\b/g, '<card>'],
  [/\b\d{3}-\d{2}-\d{4}\b/g, '<ssn>'],
  [/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, '<ip>'],
  [/eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, '<jwt>'],
  [/(?:Authorization\s*[=:]\s*)?[Bb]earer\s+[A-Za-z0-9._-]+/g, 'Bearer <token>'],
  [/(password|token|secret|api[-_]?key)\s*[=:]\s*\S+/gi, '$1=<redacted>'],
];

const scrub = (s: string | undefined): string | undefined => {
  if (!s) return s;
  return PII_PATTERNS.reduce((acc, [p, r]) => acc.replace(p, r), s);
};

const stripQueryString = (url: string | undefined): string | undefined => {
  if (!url) return url;
  const q = url.indexOf('?');
  return q === -1 ? url : url.slice(0, q);
};

const detectOsVersion = (): string | undefined => {
  if (typeof navigator === 'undefined') return undefined;
  const ua = navigator.userAgent;
  let m = ua.match(/iPhone OS (\d+(?:[._]\d+)*)/);
  if (m) return `iOS ${m[1].replace(/_/g, '.')}`;
  m = ua.match(/Android (\d+(?:\.\d+)*)/);
  if (m) return `Android ${m[1]}`;
  m = ua.match(/Mac OS X (\d+(?:[._]\d+)*)/);
  if (m) return `macOS ${m[1].replace(/_/g, '.')}`;
  m = ua.match(/Windows NT (\d+(?:\.\d+)*)/);
  if (m) return `Windows NT ${m[1]}`;
  return undefined;
};

const newSessionId = (): string => {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') {
    return `${Date.now().toString(36)}-${c.randomUUID().slice(0, 12)}`;
  }
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = new Uint8Array(8);
    c.getRandomValues(bytes);
    return `${Date.now().toString(36)}-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

class ErrorReporter {
  private appVersion = '0.0.0';
  private buildNumber = '0';
  // Default used on web builds (no native App.getInfo() available). Native
  // builds overwrite this in init() with the real Capacitor appId ('net.dentalpost.mobile').
  private bundleId = 'net.dentalpost.web';
  private osVersion?: string;
  private userId?: string;
  private sessionId = newSessionId();
  private breadcrumbs: Breadcrumb[] = [];
  private sessionSignatures = new Set<string>();
  private initialized = false;
  private currentRoute?: string;

  async init(): Promise<void> {
    if (this.initialized) return;
    if (typeof window === 'undefined') return;
    if (!errorsBaseUrl()) return;

    this.initialized = true;

    try {
      if (Capacitor.isNativePlatform()) {
        const info = await App.getInfo();
        this.appVersion = info.version;
        this.buildNumber = info.build;
        this.bundleId = info.id;
      } else {
        this.appVersion = process.env.GATSBY_APP_VERSION ?? '0.0.0';
      }
    } catch (err) {
      console.warn('errorReporter.App.getInfo failed', err);
    }
    this.osVersion = detectOsVersion();

    window.addEventListener('error', this.onError);
    window.addEventListener('unhandledrejection', this.onRejection);
    window.addEventListener('pagehide', this.flushOnPageHide);
    window.addEventListener('online', this.flushQueue);

    this.flushQueue();
  }

  setUserId(userId: string | undefined): void {
    this.userId = userId;
  }

  setRoute(route: string): void {
    this.currentRoute = route;
    this.addBreadcrumb('navigation', { route });
  }

  addBreadcrumb(type: string, data?: Record<string, string>): void {
    this.breadcrumbs.push({ ts: Date.now(), type, data });
    if (this.breadcrumbs.length > MAX_BREADCRUMBS) {
      this.breadcrumbs.shift();
    }
  }

  async report(error: Error | unknown, severity: Severity = 'error'): Promise<void> {
    if (!this.initialized) return;

    const err =
      error instanceof Error
        ? error
        : new Error(typeof error === 'string' ? error : JSON.stringify(error));

    const payload: ErrorReport = {
      appVersion: this.appVersion,
      buildNumber: this.buildNumber,
      bundleId: this.bundleId,
      platform: getPlatform(),
      osVersion: this.osVersion,
      userId: this.userId,
      sessionId: this.sessionId,
      timestamp: Date.now(),
      severity,
      error: {
        name: err.name || 'Error',
        message: scrub(err.message) ?? 'Unknown error',
        stack: scrub(err.stack),
      },
      breadcrumbs: this.breadcrumbs.slice(-15),
      context: {
        route: stripQueryString(this.currentRoute ?? (typeof window !== 'undefined' ? window.location.pathname : undefined)),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 256) : undefined,
        networkType:
          (typeof navigator !== 'undefined'
            ? (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType
            : undefined),
      },
    };

    const sigKey = `${payload.error.name}|${payload.error.message.slice(0, 200)}`;
    if (this.sessionSignatures.has(sigKey)) return;
    this.sessionSignatures.add(sigKey);

    try {
      const ok = await this.send(payload);
      if (!ok) this.enqueue(payload);
    } catch {
      this.enqueue(payload);
    }
  }

  private readAuthToken(): string | null {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return null;
      return window.localStorage.getItem(AUTH_TOKEN_KEY);
    } catch {
      return null;
    }
  }

  // The {username} path segment matches the JWT `sub` claim, which CloudFront
  // cross-checks against the verified JWT. Read fresh at send time so logout/
  // re-login mid-session is picked up.
  private readPathUsername(): string | null {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return null;
      const raw = window.localStorage.getItem(USER_DATA_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      const sub = data?.sub;
      return typeof sub === 'string' && sub.length > 0 ? sub : null;
    } catch {
      return null;
    }
  }

  private send = async (payload: ErrorReport, useBeacon = false): Promise<boolean> => {
    const authToken = this.readAuthToken();
    const pathUsername = this.readPathUsername();
    const base = errorsBaseUrl();
    // CloudFront requires a verifiable JWT and a matching {username} path
    // segment. Also need the API base URL set. Skip silently when any is
    // missing — pre-login crashes are an accepted blind spot.
    if (!authToken || !pathUsername || !base) return false;

    const url = `${base}/${encodeURIComponent(pathUsername)}`;
    const body = JSON.stringify(payload);
    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // sendBeacon can't set custom headers, so the Authorization header would
      // be lost. Fall through to fetch+keepalive (still fires on pagehide).
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken,
      },
      body,
      keepalive: true,
    });
    return res.ok;
  };

  private enqueue(payload: ErrorReport): void {
    try {
      const queue = this.readQueue();
      queue.push({ payload, enqueuedAt: Date.now() });
      while (queue.length > MAX_QUEUE) queue.shift();
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    } catch {
      // Quota exceeded or disabled; drop silently
    }
  }

  private readQueue(): QueueItem[] {
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const now = Date.now();
      return parsed.filter((item: QueueItem) => item?.payload && now - item.enqueuedAt < QUEUE_TTL_MS);
    } catch {
      return [];
    }
  }

  private writeQueue(queue: QueueItem[]): void {
    try {
      if (queue.length === 0) localStorage.removeItem(QUEUE_KEY);
      else localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    } catch {
      // ignore
    }
  }

  private flushQueue = async (): Promise<void> => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const queue = this.readQueue();
    if (queue.length === 0) {
      this.writeQueue([]);
      return;
    }

    const remaining: QueueItem[] = [];
    for (let i = 0; i < queue.length; i += FLUSH_CONCURRENCY) {
      const batch = queue.slice(i, i + FLUSH_CONCURRENCY);
      const results = await Promise.allSettled(batch.map((item) => this.send(item.payload)));
      results.forEach((r, idx) => {
        if (r.status !== 'fulfilled' || r.value !== true) remaining.push(batch[idx]);
      });
    }
    this.writeQueue(remaining);
  };

  private flushOnPageHide = (): void => {
    const queue = this.readQueue();
    let used = 0;
    for (const item of queue) {
      const size = JSON.stringify(item.payload).length;
      if (used + size > BEACON_BUDGET_BYTES) break;
      this.send(item.payload, true).catch(() => undefined);
      used += size;
    }
  };

  private onError = (event: ErrorEvent): void => {
    this.report(event.error ?? new Error(event.message), 'error').catch(() => undefined);
  };

  private onRejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason;
    const err = reason instanceof Error ? reason : new Error(String(reason));
    this.report(err, 'error').catch(() => undefined);
  };
}

export const errorReporter = new ErrorReporter();
